// High-contrast, colorblind-friendly color palette for players
// Based on Paul Tol's colorblind-friendly palette with high contrast ratios
export const PLAYER_COLORS = [
  '#0173B2', // Strong Blue - excellent contrast, distinguishable by all
  '#C46800', // Bold Orange - deeper than highway lines, colorblind safe
  '#CC78BC', // Light Purple/Pink - good contrast, unique hue
  '#029E73', // Teal Green - distinct from blue, good contrast
  '#D55E00', // Vermillion Red - high contrast, colorblind safe
  '#8B008B', // Dark Magenta - distinct from pink and purple
  '#FBAFE4', // Light Pink - soft but visible, unique
  '#949494', // Medium Gray - neutral, good contrast
  '#E63946', // Crimson Red - bold, distinct from vermillion and orange
  '#56B4E9', // Sky Blue - lighter blue variant
  '#6A4C93', // Deep Purple - distinct from all other hues
  '#2D6A4F', // Forest Green - darker, distinct from teal
];

/**
 * Creates a stable player-to-color mapping.
 * Uses stored playerColor if set, otherwise auto-assigns from unused colors in PLAYER_COLORS.
 */
export function getPlayerColorMap(players: { name: string; playerColor?: string | null }[]): Record<string, string> {
  const colorMap: Record<string, string> = {};
  const usedColors = new Set<string>();

  // First pass: assign stored colors
  for (const player of players) {
    if (player.playerColor) {
      colorMap[player.name] = player.playerColor;
      usedColors.add(player.playerColor);
    }
  }

  // Second pass: auto-assign from unused palette colors for players without a stored color
  const sorted = [...players].filter(p => !p.playerColor).sort((a, b) => a.name.localeCompare(b.name));
  for (const player of sorted) {
    const available = PLAYER_COLORS.find(c => !usedColors.has(c));
    const color = available || PLAYER_COLORS[sorted.indexOf(player) % PLAYER_COLORS.length];
    colorMap[player.name] = color;
    usedColors.add(color);
  }

  return colorMap;
}

/**
 * Returns a random unused color from PLAYER_COLORS given a list of players with stored colors.
 */
export function getRandomAvailableColor(players: { playerColor?: string | null }[]): string {
  const usedColors = new Set(players.map(p => p.playerColor).filter(Boolean));
  const available = PLAYER_COLORS.filter(c => !usedColors.has(c));
  if (available.length === 0) {
    return PLAYER_COLORS[Math.floor(Math.random() * PLAYER_COLORS.length)];
  }
  return available[Math.floor(Math.random() * available.length)];
}
